// =============================================================================
// Phase 1 SOC / Wazuh Integration: Wazuh Configuration Form
// =============================================================================

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/hooks/useAuth';
import { useSecurityStore } from '@/stores/securityStore';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Save, TestTube } from 'lucide-react';
import type { WazuhConfigFormData } from '@/types/security';

const ENCRYPTION_KEY = 'wazuh-soc-phase1-key';

function encryptPassword(password: string): string {
  let result = '';
  for (let i = 0; i < password.length; i++) {
    result += String.fromCharCode(
      password.charCodeAt(i) ^ ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length)
    );
  }
  return btoa(result);
}

interface Site { id: string; name: string; }

export function WazuhConfigForm() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;
  
  const { wazuhConfig, configLoading, configError, saveWazuhConfig } = useSecurityStore();
  const [sites, setSites] = useState<Site[]>([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<WazuhConfigFormData>({
    defaultValues: {
      api_url: '', api_user: '', api_password: '', verify_ssl: true,
      sync_enabled: false, sync_interval_minutes: 15, online_threshold_minutes: 5, default_site_id: null,
    },
  });
  
  const syncEnabled = watch('sync_enabled');
  const verifySsl = watch('verify_ssl');
  
  useEffect(() => {
    if (orgId) {
      supabase.from('sites').select('id, name').eq('org_id', orgId).then(({ data }) => setSites(data || []));
    }
  }, [orgId]);
  
  useEffect(() => {
    if (wazuhConfig) {
      setValue('api_url', wazuhConfig.api_url);
      setValue('api_user', wazuhConfig.api_user);
      setValue('verify_ssl', wazuhConfig.verify_ssl);
      setValue('sync_enabled', wazuhConfig.sync_enabled);
      setValue('sync_interval_minutes', wazuhConfig.sync_interval_minutes);
      setValue('online_threshold_minutes', wazuhConfig.online_threshold_minutes);
      setValue('default_site_id', wazuhConfig.default_site_id);
    }
  }, [wazuhConfig, setValue]);
  
  const onSubmit = async (data: WazuhConfigFormData) => {
    if (!orgId) return;
    setSaving(true);
    try {
      const configData: Record<string, unknown> = {
        api_url: data.api_url, api_user: data.api_user, verify_ssl: data.verify_ssl,
        sync_enabled: data.sync_enabled, sync_interval_minutes: data.sync_interval_minutes,
        online_threshold_minutes: data.online_threshold_minutes, default_site_id: data.default_site_id || null,
      };
      if (data.api_password) {
        configData.api_password_encrypted = encryptPassword(data.api_password);
      } else if (!wazuhConfig) {
        toast.error('Password is required');
        setSaving(false);
        return;
      }
      await saveWazuhConfig(orgId, configData);
      toast.success('Configuration saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  const handleTestConnection = async () => {
    if (!orgId) return;
    setTesting(true);
    try {
      const result = await useSecurityStore.getState().triggerSync(orgId);
      if (result.success) toast.success('Connection successful!');
      else toast.error('Failed: ' + result.errors.join(', '));
    } catch {
      toast.error('Test failed');
    } finally {
      setTesting(false);
    }
  };
  
  if (configLoading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {configError && <div className="p-3 rounded-md bg-danger/10 text-danger text-sm">{configError}</div>}
      
      <div className="space-y-4">
        <h3 className="text-sm font-medium">Connection Settings</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="api_url">Wazuh API URL</Label>
            <Input id="api_url" placeholder="https://wazuh-manager.local:55000" {...register('api_url', { required: 'Required' })} />
            {errors.api_url && <p className="text-xs text-danger">{errors.api_url.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="api_user">API Username</Label>
            <Input id="api_user" placeholder="wazuh-api-user" {...register('api_user', { required: 'Required' })} />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="api_password">API Password {wazuhConfig && <span className="text-xs text-muted-foreground">(leave blank to keep)</span>}</Label>
            <Input id="api_password" type="password" placeholder="••••••••" {...register('api_password')} />
          </div>
          <div className="flex items-center space-x-2 pt-8">
            <Switch id="verify_ssl" checked={verifySsl} onCheckedChange={(c) => setValue('verify_ssl', c)} />
            <Label htmlFor="verify_ssl">Verify SSL</Label>
          </div>
        </div>
      </div>
      
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-medium">Sync Settings</h3>
        <div className="flex items-center space-x-2">
          <Switch id="sync_enabled" checked={syncEnabled} onCheckedChange={(c) => setValue('sync_enabled', c)} />
          <Label htmlFor="sync_enabled">Enable Automatic Sync <span className="text-xs text-muted-foreground block">TODO Phase 2</span></Label>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Sync Interval (minutes)</Label>
            <Input type="number" min={5} max={1440} {...register('sync_interval_minutes', { valueAsNumber: true })} />
          </div>
          <div className="space-y-2">
            <Label>Online Threshold (minutes)</Label>
            <Input type="number" min={1} max={60} {...register('online_threshold_minutes', { valueAsNumber: true })} />
          </div>
        </div>
      </div>
      
      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-sm font-medium">Default Site</h3>
        <Select value={watch('default_site_id') || 'none'} onValueChange={(v) => setValue('default_site_id', v === 'none' ? null : v)}>
          <SelectTrigger><SelectValue placeholder="No default site" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No default site</SelectItem>
            {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      
      <div className="flex justify-end gap-3 pt-4 border-t">
        {wazuhConfig && (
          <Button type="button" variant="outline" onClick={handleTestConnection} disabled={testing || saving}>
            {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TestTube className="h-4 w-4 mr-2" />}
            Test Connection
          </Button>
        )}
        <Button type="submit" disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save
        </Button>
      </div>
    </form>
  );
}
